// Media & documenten tile — the binary + write calls the generic Refine dataProvider can't make.
// Uploads are multipart and downloads are bytes, and the access token is bearer-only (in memory, not a
// cookie), so a plain <img src>/<a href> can't carry it — every call attaches the bearer here and the
// image/preview flows go through an authenticated blob fetch → object URL.
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

export interface MediaTag {
  id: number;
  label: string;
  color: string | null;
}

export interface MediaItem {
  id: number;
  fileName: string | null;
  contentType: string | null;
  fileSizeBytes: number | null;
  uploadedAt: string | null;
  description: string | null;
  category: string | null;
  version: number | null;
  tags: MediaTag[];
  /** Of het gekoppelde pleeggezin dit bestand mag zien (ontbreekt bij een oudere api: dan "ja"). */
  zichtbaarPleeggezin?: boolean;
}

/** An HTTP failure carrying the status code, so callers can branch on 409 (concurrent edit). */
export class MediaApiError extends Error {
  readonly statusCode: number;
  constructor(statusCode: number, message?: string) {
    super(message ?? `HTTP ${statusCode}`);
    this.statusCode = statusCode;
    this.name = "MediaApiError";
  }
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function base(animalId: string): string {
  return `${API_URL}/animals/${animalId}/media`;
}

/** Upload one or more files with a shared description + tag selection. */
export async function uploadMedia(
  animalId: string,
  files: File[],
  description: string,
  tagIds: number[],
  zichtbaarPleeggezin = true,
): Promise<MediaItem[]> {
  const form = new FormData();
  for (const file of files) form.append("files", file);
  if (description.trim()) form.append("description", description.trim());
  for (const id of tagIds) form.append("tagIds", String(id));
  form.append("zichtbaarPleeggezin", String(zichtbaarPleeggezin));

  const res = await fetch(base(animalId), {
    method: "POST",
    headers: authHeaders(), // no Content-Type — the browser sets the multipart boundary
    body: form,
  });
  if (!res.ok) throw new MediaApiError(res.status, res.statusText);
  return (await res.json()) as MediaItem[];
}

/** Update a file's description + tags; `version` round-trips the optimistic-lock token. */
export async function updateMedia(
  animalId: string,
  fileId: number,
  payload: { description: string | null; tagIds: number[]; version: number | null; zichtbaarPleeggezin?: boolean },
): Promise<MediaItem> {
  const res = await fetch(`${base(animalId)}/${fileId}`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json", Accept: "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new MediaApiError(res.status, res.statusText);
  return (await res.json()) as MediaItem;
}

/** Soft-delete a file. */
export async function deleteMedia(animalId: string, fileId: number): Promise<void> {
  const res = await fetch(`${base(animalId)}/${fileId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new MediaApiError(res.status, res.statusText);
}

/**
 * Draait een afbeelding een kwartslag — `POST /api/animals/{id}/media/{fileId}/rotate`.
 *
 * <p>Een foto die op zijn kant het dossier in komt is anders niet te lezen. De server herschrijft de
 * bytes, dus de gedraaide versie gaat ook mee naar de pdf en de advertentie.
 */
export async function rotateMedia(
  animalId: string,
  fileId: number,
  clockwise = true,
): Promise<MediaItem> {
  const res = await fetch(`${base(animalId)}/${fileId}/rotate?clockwise=${clockwise}`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) throw new MediaApiError(res.status, res.statusText);
  return (await res.json()) as MediaItem;
}

/** Fetch a file's bytes (authenticated) as a Blob — for image thumbnails, preview and download. */
export async function fetchMediaBlob(
  animalId: string,
  fileId: number,
  disposition: "inline" | "attachment" = "inline",
): Promise<Blob> {
  const res = await fetch(`${base(animalId)}/${fileId}/content?disposition=${disposition}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new MediaApiError(res.status, res.statusText);
  return res.blob();
}

/** Trigger a browser download of a file via a temporary object-URL anchor. */
export async function downloadMedia(animalId: string, fileId: number, fileName: string): Promise<void> {
  const blob = await fetchMediaBlob(animalId, fileId, "attachment");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "bestand";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
